import { useState } from "react";

import {
  EASY_CONFIG,
  EXPERT_CONFIG,
  HARD_CONFIG,
  HUGE_CONFIG,
  NORMAL_CONFIG,
  OCEAN_CONFIG,
  coordLabel,
  newBoard,
  parseCoord,
  revealCell,
  type Board,
  type GameConfig,
  type RevealResult,
} from "@nav-games/soundings";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { CardinalBuoy, CellFill, RockGlyph, WaveGlyph } from "../../ui/SoundingsGlyphs";

interface SoundingsProps {
  onNavigate: (to: string) => void;
}

interface Difficulty {
  id: "easy" | "normal" | "hard" | "huge" | "expert" | "ocean";
  label: string;
  config: GameConfig;
  blurb: string;
  /// Charts larger than 10 × 10. Hidden on small screens where the
  /// grid would scroll well off-screen — they need a tablet/desktop.
  bigBoard?: boolean;
}

/// True on a phone-sized viewport. Used to hide the big charts, which
/// are unusable without a lot of scrolling on a small screen.
function isCompactViewport(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 760px)").matches
  );
}

const DIFFICULTIES: readonly Difficulty[] = [
  {
    id: "easy",
    label: "Easy",
    config: EASY_CONFIG,
    blurb:
      "8 × 8 chart, three ships, three hazards — every hazard marked by a cardinal. Four lives.",
  },
  {
    id: "normal",
    label: "Normal",
    config: NORMAL_CONFIG,
    blurb:
      "10 × 10 chart, full five-ship fleet, five hazards (four marked, one unmarked). Three lives.",
  },
  {
    id: "hard",
    label: "Hard",
    config: HARD_CONFIG,
    blurb:
      "10 × 10 chart, full fleet, seven hazards but only four marked. Two lives — careful out there.",
  },
  {
    id: "huge",
    label: "Huge",
    config: HUGE_CONFIG,
    blurb:
      "30 × 30 chart with a nine-ship fleet, 18 hazards (12 marked). Five lives. Search problem becomes real — type coordinates to fire fast.",
    bigBoard: true,
  },
  {
    id: "expert",
    label: "Expert",
    config: EXPERT_CONFIG,
    blurb:
      "50 × 50 chart, 15 ships, 30 hazards (20 marked). Six lives. A serious session — definitely use the coordinate input.",
    bigBoard: true,
  },
  {
    id: "ocean",
    label: "Ocean",
    config: OCEAN_CONFIG,
    blurb:
      "100 × 100 chart, 24-ship fleet, 80 hazards (50 marked). Eight lives. Expect 250+ shots even with perfect cardinal deduction. Best on a tablet.",
    bigBoard: true,
  },
];

export function Soundings({ onNavigate }: SoundingsProps) {
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  if (!difficulty) {
    return (
      <Picker
        onStart={setDifficulty}
        onBack={() => onNavigate("/play")}
      />
    );
  }
  return (
    <Game
      difficulty={difficulty}
      onBack={() => setDifficulty(null)}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (d: Difficulty) => void;
  onBack: () => void;
}) {
  const compact = isCompactViewport();
  return (
    <>
      <h2 style={{ marginTop: 0 }}>Soundings</h2>
      <p className="muted">
        A chart-search game. A hidden fleet is anchored somewhere on
        the grid. So are some rocks. Most rocks have an{" "}
        <strong>IALA cardinal mark</strong> in an adjacent cell —
        revealing the mark tells you which side is the safe passage,
        and therefore where the rock sits.
      </p>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>How to play</h3>
        <ul className="small" style={{ paddingLeft: 18 }}>
          <li>
            Tap a cell to take a <strong>sounding</strong>. You'll
            see water, a cardinal mark, or you'll find a ship — or
            ground on a rock.
          </li>
          <li>
            <strong>Cardinal mark</strong> — North cardinal means
            "pass to the north", so the rock is one cell to the south.
            East cardinal → rock to the west. And so on.
          </li>
          <li>
            <strong>Hits</strong> on a ship are recorded. When every
            cell of a ship is hit it's <strong>sunk</strong>.
          </li>
          <li>
            <strong>Grounding</strong> on a rock costs a life — and{" "}
            <strong>two</strong> if you'd already revealed the cardinal
            marking it (you were warned). Run out of lives and it's over.
          </li>
          <li>
            <strong>Land</strong> (on the bigger charts) is safe — tap it
            and the whole shore is charted at once. But rocks lurk just
            offshore in these rocky enclaves, so watch the cardinals.
          </li>
          <li>
            <strong>Win</strong> by sinking the whole fleet.
          </li>
        </ul>
      </div>
      {DIFFICULTIES.filter((d) => !compact || !d.bigBoard).map((d) => (
        <div key={d.id} className="card">
          <h3 style={{ marginTop: 0 }}>{d.label}</h3>
          <p className="small muted" style={{ marginBottom: 12 }}>
            {d.blurb}
          </p>
          <button onClick={() => onStart(d)}>Start {d.label.toLowerCase()}</button>
        </div>
      ))}
      {compact && (
        <p className="small muted">
          The larger charts (30 × 30 and up) are hidden on a small screen —
          open nav-games on a tablet or desktop to play those.
        </p>
      )}
      <button className="secondary" onClick={onBack}>
        ← Back to Play
      </button>
    </>
  );
}

function Game({
  difficulty,
  onBack,
}: {
  difficulty: Difficulty;
  onBack: () => void;
}) {
  // Generate a fresh board for the current difficulty when the
  // player picks "Play again" — we change the key to force a new
  // instance.
  const [gameKey, setGameKey] = useState(0);
  const [board, setBoard] = useState<Board>(() => newBoard(difficulty.config));
  const [lastEvent, setLastEvent] = useState<RevealResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const onClick = (col: number, row: number) => {
    if (board.status !== "playing") return;
    const { board: next, result } = revealCell(board, col, row, {
      revealPaired: true,
    });
    setBoard({ ...next, cells: next.cells.map((row) => [...row]) });
    setLastEvent(result);
  };

  const restart = () => {
    setGameKey((k) => k + 1);
    setBoard(newBoard(difficulty.config));
    setLastEvent(null);
  };

  return (
    <div key={gameKey}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Soundings — {difficulty.label}</h2>
        <span className="pill">Lives {"♥".repeat(board.lives)}</span>
        <span className="pill">Shots {board.shotsFired}</span>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "buoyage" : null} onClose={() => setHelpOpen(false)} />

      {board.status === "won" && (
        <div
          className="card"
          style={{ borderColor: "var(--success)", borderWidth: 2 }}
        >
          <h3 style={{ marginTop: 0 }}>Fleet sunk — well sailed.</h3>
          <p className="small muted">
            {board.shotsFired} shots, {board.lives} lives remaining.
          </p>
          <button onClick={restart}>Play another</button>{" "}
          <button className="secondary" onClick={onBack}>
            Change difficulty
          </button>
        </div>
      )}

      {board.status === "lost" && (
        <div
          className="card"
          style={{ borderColor: "var(--danger)", borderWidth: 2 }}
        >
          <h3 style={{ marginTop: 0 }}>Aground for the last time.</h3>
          <p className="small muted">
            All lives lost. {board.shotsFired} shots fired,{" "}
            {board.ships.filter((s) => s.sunk).length} ships sunk.
          </p>
          <button onClick={restart}>Try again</button>{" "}
          <button className="secondary" onClick={onBack}>
            Change difficulty
          </button>
        </div>
      )}

      {board.status === "playing" && lastEvent && (
        <EventToast event={lastEvent} />
      )}

      <CoordInput board={board} onFire={onClick} />

      <Grid board={board} onClick={onClick} />

      <FleetPanel board={board} />

      <button
        className="secondary"
        onClick={onBack}
        style={{ marginTop: 16 }}
      >
        ← Back to picker
      </button>
    </div>
  );
}

function Grid({
  board,
  onClick,
}: {
  board: Board;
  onClick: (col: number, row: number) => void;
}) {
  const { cols, rows } = board.config;
  // Each cell sized via CSS. On small boards we let cells grow up
  // to 40 px; on huge boards we drop to a fixed pixel size so the
  // grid stays clickable (and overflows into a scroll container
  // when the chart is bigger than the screen). The 14 px floor is
  // the smallest comfortable tap target.
  const cellSize =
    cols >= 80
      ? "14px"
      : cols >= 40
        ? "18px"
        : cols >= 20
          ? "24px"
          : `min(${100 / (cols + 1)}vw, 40px)`;
  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    color: "var(--text-muted)",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  return (
    <div
      className="card"
      style={{
        padding: 8,
        overflow: "auto",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `auto repeat(${cols}, ${cellSize})`,
          gridTemplateRows: `auto repeat(${rows}, ${cellSize})`,
          gap: 2,
          width: "fit-content",
          margin: "0 auto",
        }}
      >
        {/* Top-left blank */}
        <div />
        {/* Column headers (A, B, C…) */}
        {Array.from({ length: cols }, (_, c) => (
          <div key={`ch-${c}`} style={labelStyle}>
            {String.fromCharCode(65 + c)}
          </div>
        ))}
        {/* Rows */}
        {Array.from({ length: rows }, (_, r) => (
          <FragmentRow
            key={`row-${r}`}
            rowIdx={r}
            cols={cols}
            board={board}
            onClick={onClick}
            labelStyle={labelStyle}
          />
        ))}
      </div>
    </div>
  );
}

function FragmentRow({
  rowIdx,
  cols,
  board,
  onClick,
  labelStyle,
}: {
  rowIdx: number;
  cols: number;
  board: Board;
  onClick: (col: number, row: number) => void;
  labelStyle: React.CSSProperties;
}) {
  return (
    <>
      <div style={labelStyle}>{rowIdx + 1}</div>
      {Array.from({ length: cols }, (_, c) => (
        <CellTile
          key={`${c}-${rowIdx}`}
          col={c}
          row={rowIdx}
          board={board}
          onClick={onClick}
        />
      ))}
    </>
  );
}

function CellTile({
  col,
  row,
  board,
  onClick,
}: {
  col: number;
  row: number;
  board: Board;
  onClick: (col: number, row: number) => void;
}) {
  const cell = board.cells[row]![col]!;
  const isPlaying = board.status === "playing";
  const isClickable = isPlaying && cell.state === "hidden";

  return (
    <button
      onClick={() => isClickable && onClick(col, row)}
      disabled={!isClickable}
      title={coordLabel(col, row)}
      aria-label={`${coordLabel(col, row)}${stateAriaLabel(cell.state, cell.content)}`}
      style={{
        ...cellStyle(cell.state),
        minHeight: 0,
        padding: 0,
        fontSize: 11,
        cursor: isClickable ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <CellGlyph cell={cell} />
    </button>
  );
}

function CellGlyph({ cell }: { cell: Board["cells"][number][number] }) {
  switch (cell.state) {
    case "hidden":
      return null;
    case "water":
      return (
        <CellFill>
          <WaveGlyph />
        </CellFill>
      );
    case "cardinal":
      if (cell.content.kind !== "cardinal") return null;
      return (
        <CellFill>
          <CardinalBuoy direction={cell.content.direction} />
        </CellFill>
      );
    case "grounded":
      return (
        <CellFill>
          <RockGlyph aground />
        </CellFill>
      );
    case "land":
      // Colour-only — the fill reads as land at any cell size.
      return null;
    case "hit":
      return <span style={{ fontSize: 14, fontWeight: 700 }}>✕</span>;
    case "sunk":
      return <span style={{ fontSize: 14, fontWeight: 700 }}>✕</span>;
  }
}

function cellStyle(state: Board["cells"][number][number]["state"]): React.CSSProperties {
  switch (state) {
    case "hidden":
      return {
        background: "#1e6091",
        color: "#ffffff",
        border: "1px solid #164a72",
      };
    case "water":
      return {
        background: "#eaf3fb",
        color: "#5d6878",
        border: "1px solid #cdd4dd",
      };
    case "cardinal":
      return {
        background: "#ffffff",
        color: "#1c2530",
        border: "1px solid #cdd4dd",
        padding: 0,
      };
    case "hit":
      return {
        background: "#fca5a5",
        color: "#7f1d1d",
        border: "1px solid #b91c1c",
      };
    case "sunk":
      return {
        background: "#7f1d1d",
        color: "#ffffff",
        border: "1px solid #450a0a",
      };
    case "grounded":
      return {
        background: "#a16207",
        color: "#fef3c7",
        border: "1px solid #713f12",
      };
    case "land":
      return {
        background: "#cdb892",
        color: "#3f3a2c",
        border: "1px solid #b6a075",
      };
  }
}

function stateAriaLabel(
  state: Board["cells"][number][number]["state"],
  content: Board["cells"][number][number]["content"],
): string {
  switch (state) {
    case "hidden":
      return " — hidden";
    case "water":
      return " — water";
    case "cardinal":
      return ` — ${content.kind === "cardinal" ? content.direction : ""} cardinal mark`;
    case "hit":
      return " — hit";
    case "sunk":
      return " — sunk";
    case "grounded":
      return " — grounded on a rock";
    case "land":
      return " — land";
  }
}

/// Coordinate-input fast-fire box. Type "C7" / "AB42" / case
/// insensitive; Enter or the Fire button sends. Mostly aimed at
/// the 50 × 50 and 100 × 100 boards where clicking a 14 px cell
/// on a phone is awkward, but it works at every size.
function CoordInput({
  board,
  onFire,
}: {
  board: Board;
  onFire: (col: number, row: number) => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (board.status !== "playing") return null;
  const submit = () => {
    const parsed = parseCoord(value, board.config.cols, board.config.rows);
    if (!parsed) {
      setError(
        `"${value}" isn't a valid coordinate (A1 – ${coordLabel(board.config.cols - 1, board.config.rows - 1)}).`,
      );
      return;
    }
    const cell = board.cells[parsed.row]![parsed.col]!;
    if (cell.state !== "hidden") {
      setError(`${coordLabel(parsed.col, parsed.row)} already revealed.`);
      return;
    }
    onFire(parsed.col, parsed.row);
    setValue("");
    setError(null);
  };
  return (
    <div
      className="card"
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <label className="small muted" htmlFor="coord-input">
        Fire at coordinate
      </label>
      <input
        id="coord-input"
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={`E.g. ${coordLabel(2, 6)}`}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        style={{
          width: 110,
          textTransform: "uppercase",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      />
      <button onClick={submit} disabled={!value.trim()}>
        Fire
      </button>
      {error && (
        <span
          className="small"
          style={{ color: "var(--danger)", flexBasis: "100%" }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

function EventToast({ event }: { event: RevealResult }) {
  const text = (() => {
    switch (event.event) {
      case "water":
        return "Open water.";
      case "cardinal":
        return "Cardinal mark — the rock is on the opposite side.";
      case "land":
        return "Land — safe. Rocks may lurk just offshore.";
      case "hit":
        return "Hit!";
      case "sunk":
        return `${event.sunkShipName} sunk!`;
      case "grounded":
        return event.doublePenalty
          ? "Aground! You'd seen the cardinal — that's two lives."
          : "Aground — you lose a life.";
      case "already-revealed":
        return null;
      case "game-over":
        return null;
    }
  })();
  if (!text) return null;
  const tone =
    event.event === "grounded"
      ? "danger"
      : event.event === "sunk" || event.event === "hit"
        ? "success"
        : "muted";
  const colour =
    tone === "danger"
      ? "var(--danger)"
      : tone === "success"
        ? "var(--success)"
        : "var(--text-muted)";
  return (
    <div
      role="status"
      aria-live="polite"
      className="card"
      style={{
        padding: 12,
        marginBottom: 12,
        borderLeft: `4px solid ${colour}`,
      }}
    >
      <span className="small">{text}</span>
    </div>
  );
}

function FleetPanel({ board }: { board: Board }) {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Fleet</h3>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {board.ships.map((s) => (
          <li
            key={s.spec.id}
            className="small"
            style={{
              marginBottom: 4,
              textDecoration: s.sunk ? "line-through" : "none",
              color: s.sunk ? "var(--text-muted)" : undefined,
            }}
          >
            {s.spec.name} ({s.spec.length}) — {s.hits} / {s.cells.length}{" "}
            {s.sunk ? "sunk" : "afloat"}
          </li>
        ))}
      </ul>
    </div>
  );
}
